import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { useAuth } from "./contexts/AuthContext";
import NotFound from "pages/NotFound";
import PaymentMethodPerformance from './pages/payment-method-performance';
import MerchantAnalytics from './pages/merchant-analytics';
import FinancialReconciliation from './pages/financial-reconciliation';
import TransactionOverview from './pages/transaction-overview';
import FraudDetectionCenter from './pages/fraud-detection-center';
import UserManagementDashboard from './pages/user-management-dashboard';
import RolePermissionsManagement from './pages/role-permissions-management';
import UserActivityAuditLog from './pages/user-activity-audit-log';
import NotificationCenter from './pages/notification-center';
import NotificationPreferencesManagement from './pages/notification-preferences-management';
import PaymentGatewayConfiguration from './pages/payment-gateway-configuration';
import MerchantListing from './pages/merchant-listing';
import MerchantDetails from './pages/merchant-details';
import MerchantOnboardingStatus from './pages/merchant-onboarding-status';
import MerchantTransactionOverview from './pages/merchant-transaction-overview';
import AutomatedSettlementReconciliationCenter from 'pages/automated-settlement-reconciliation-center';
import ComprehensiveReportsCenter from 'pages/comprehensive-reports-center';

// Role-based route protection wrapper
const ProtectedRoute = ({ children, allowedRoles = [], portalRestriction = null }) => {
  const { userProfile, currentPortal, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Check portal restriction
  if (portalRestriction && currentPortal !== portalRestriction) {
    return <Navigate to="/" replace />;
  }

  // Check role-based access
  const userRole = userProfile?.role || 'merchant';
  if (allowedRoles?.length > 0 && !allowedRoles?.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Define your route here */}
        <Route path="/" element={<TransactionOverview />} />
        <Route path="/payment-method-performance" element={<PaymentMethodPerformance />} />
        <Route 
          path="/merchant-analytics" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'analyst']}>
              <MerchantAnalytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/merchants" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'analyst']}>
              <MerchantListing />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/merchants/:id" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'analyst']}>
              <MerchantDetails />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/merchants/:id/onboarding" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'analyst']}>
              <MerchantOnboardingStatus />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/merchants/:id/transactions" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'analyst']}>
              <MerchantTransactionOverview />
            </ProtectedRoute>
          } 
        />
        <Route path="/financial-reconciliation" element={<FinancialReconciliation />} />
        <Route path="/automated-settlement-reconciliation-center" element={<AutomatedSettlementReconciliationCenter />} />
        <Route 
          path="/comprehensive-reports-center" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'analyst']}>
              <ComprehensiveReportsCenter />
            </ProtectedRoute>
          } 
        />
        <Route path="/transaction-overview" element={<TransactionOverview />} />
        <Route 
          path="/fraud-detection-center" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'analyst']}>
              <FraudDetectionCenter />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/user-management-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UserManagementDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/role-permissions-management" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <RolePermissionsManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/user-activity-audit-log" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UserActivityAuditLog />
            </ProtectedRoute>
          } 
        />
        <Route path="/notification-center" element={<NotificationCenter />} />
        <Route path="/notification-preferences-management" element={<NotificationPreferencesManagement />} />
        <Route 
          path="/payment-gateway-configuration" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PaymentGatewayConfiguration />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
